import React from "react";
import { cn } from "@/utils/utils";
import { restrictedMarkdownToHtml } from "@/components/controls/TextEditor/utils/restrictedMarkdown";
import { ProposalSlot, ProposalSlotBox } from "../types";
import { SlotValues, slotValue } from "./blanks";
import { SLOT_FORMATS, SLOT_FORMATTED } from "./slotFormats";
import type { SampleColor } from "./PdfDocument";

interface Props {
  slots: ProposalSlot[];
  values: SlotValues;
  // The values the rendered document was composed with.
  composedValues: SlotValues;
  pageNumber: number;
  scale: number;
  sampleColor?: SampleColor;
  // The slot whose field has focus.
  highlightedSlotId?: string;
}

const PAPER = "white";
const INK = "#000000";

// The page's own background under a box, read at its right end inside the
// padding: a spot templates leave as background, clear of the text and of a
// blank's marker. Falls back to paper until the page has drawn.
const backgroundUnder = (box: ProposalSlotBox, sampleColor?: SampleColor) =>
  sampleColor?.(
    box.x + box.width - (box.padding ?? 0) - 1,
    box.y + box.height / 2
  ) ?? PAPER;

// Where a box sits on the page at this scale.
const boxPosition = (box: ProposalSlotBox, scale: number) => ({
  left: box.x * scale,
  top: box.y * scale,
  width: box.width * scale
});

// Drawn the way the composer draws the box. A one-line slot is centred in the
// box: the composer centres the capitals, this centres the line, so the rendered
// font's ascent and descent can shift it by up to about a tenth of the font size.
// A multiline slot starts at the top.
const boxStyle = (
  box: ProposalSlotBox,
  multiline: boolean,
  scale: number
): React.CSSProperties => {
  const padding = (box.padding ?? 0) * scale;
  return {
    ...boxPosition(box, scale),
    minHeight: box.height * scale,
    fontSize: box.fontSize * scale,
    fontWeight: box.font?.endsWith("-Bold") ? "bold" : "normal",
    fontStyle: box.font?.endsWith("-Oblique") ? "italic" : "normal",
    color: box.color ?? INK,
    ...(multiline
      ? { padding, lineHeight: 1.4 }
      : {
          display: "flex",
          alignItems: "center",
          paddingLeft: padding,
          paddingRight: padding,
          lineHeight: 1
        })
  };
};

// Shows what the rep typed at once, over every spot the value takes on the
// page, until a recompose brings back a document that already holds it. Text is
// laid out inside each box only, so a long value is approximate until then.
export const SlotOverlay: React.FC<Props> = ({
  slots,
  values,
  composedValues,
  pageNumber,
  scale,
  sampleColor,
  highlightedSlotId
}) => (
  <>
    {slots.flatMap((slot) => {
      const value = slotValue(slot, values);
      if (value === slotValue(slot, composedValues)) return [];
      return (slot.boxes ?? []).flatMap((box, index) =>
        box.page !== pageNumber
          ? []
          : [
              <div
                key={`${slot.id}-${index}`}
                className={cn(
                  "absolute pointer-events-none overflow-visible break-words font-[Helvetica,Arial,sans-serif]",
                  slot.multiline ? SLOT_FORMATTED : "whitespace-pre-wrap"
                )}
                style={{
                  ...boxStyle(box, Boolean(slot.multiline), scale),
                  backgroundColor: backgroundUnder(box, sampleColor)
                }}
                data-test={`proposal-overlay-${slot.id}`}
                // The parser escapes any HTML in the value.
                {...(slot.multiline
                  ? {
                      dangerouslySetInnerHTML: {
                        __html: restrictedMarkdownToHtml(value, SLOT_FORMATS)
                      }
                    }
                  : { children: value })}
              />
            ]
      );
    })}
    {/* Where each slot sits: the pane scrolls to it, and it tints over any typed value. */}
    {slots.flatMap((slot) =>
      (slot.boxes ?? []).flatMap((box, index) =>
        box.page !== pageNumber
          ? []
          : [
              <div
                key={`${slot.id}-anchor-${index}`}
                aria-hidden
                className={cn(
                  "absolute pointer-events-none",
                  slot.id === highlightedSlotId &&
                    "rounded-sm bg-badges-natural"
                )}
                style={{
                  ...boxPosition(box, scale),
                  height: box.height * scale
                }}
                data-slot-anchor={slot.id}
              />
            ]
      )
    )}
  </>
);
